import { useCallback, useMemo, useRef, useState } from "react";

/**
 * Download-selection state for the main (non-admin) grid.
 *
 * Deliberately in-memory only (no sessionStorage): unlike the admin disposition
 * queue, a stale download selection surviving a reload is more confusing than
 * useful. Supports single-toggle, shift-click range, and marquee/drag selection.
 */
export function useSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Anchor for shift-click range selection — the last individually-toggled card.
  const anchorRef = useRef<string | null>(null);

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    anchorRef.current = id;
  }, []);

  /**
   * Shift-click: add every id between the anchor and `id` (inclusive) in the
   * given display order. With no prior anchor it behaves like a plain toggle.
   */
  const selectRange = useCallback(
    (orderedIds: string[], id: string) => {
      const anchor = anchorRef.current;
      if (anchor == null) {
        toggle(id);
        return;
      }
      const i = orderedIds.indexOf(anchor);
      const j = orderedIds.indexOf(id);
      if (i === -1 || j === -1) {
        toggle(id);
        return;
      }
      const [lo, hi] = i <= j ? [i, j] : [j, i];
      const range = orderedIds.slice(lo, hi + 1);
      setSelected((prev) => {
        const next = new Set(prev);
        for (const r of range) next.add(r);
        return next;
      });
      anchorRef.current = id;
    },
    [toggle],
  );

  /** Marquee / select-all. additive=false replaces the selection. */
  const setMany = useCallback((ids: string[], additive: boolean) => {
    setSelected((prev) => {
      const next = additive ? new Set(prev) : new Set<string>();
      for (const id of ids) next.add(id);
      return next;
    });
    const last = ids[ids.length - 1];
    if (last != null) anchorRef.current = last;
  }, []);

  const remove = useCallback((id: string) => {
    setSelected((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelected(new Set());
    anchorRef.current = null;
  }, []);

  const api = useMemo(
    () => ({ selected, count: selected.size, isSelected, toggle, selectRange, setMany, remove, clear }),
    [selected, isSelected, toggle, selectRange, setMany, remove, clear],
  );
  return api;
}

export type Selection = ReturnType<typeof useSelection>;
